if (!window.customElements.get('choose-offer-form')) {
  const WINBACKS_SELECTED_OFFER_STORAGE_KEY = 'winbacks:selected-offer';
  const WINBACKS_SELECTED_DISCOUNT_STORAGE_KEY = 'winbacks:selected-reference';
  const WINBACKS_PARENT_SUBSCRIPTION_STORAGE_KEY = 'winbacks:parent-subscription';

  class ChooseOfferForm extends HTMLElement {
    constructor() {
      super();
      this.offerTiles = this.querySelectorAll('[data-offer-tile]');
      this.form = this.querySelector('#newsletter-form');
      this.emailInput = this.querySelector('input[name="contact[email]"]');
      this.builderUrl = this.dataset.builderUrl;
      this.discount = this.querySelector('[data-reference-input]')?.value;
      this.isWinbacksFlow = this.dataset.winbacksFlow === 'true';
      this.submitButton = this.querySelector('[data-cyo-submit]');
    }
    connectedCallback() {
      const listContainer = this.querySelector('[role="list"]');
      if (listContainer) {
        listContainer.setAttribute('role', 'radiogroup');
        listContainer.setAttribute('aria-label', 'Choose an offer');
      }

      if (this.submitButton) {
        this.submitButton.addEventListener('click', () => this.handleRemoveStoredDiscount());
      }
      this.offerTiles.forEach((tile) => {
        const isSelected = tile.classList.contains('selected');
        tile.setAttribute('tabindex', isSelected ? '0' : '-1');

        tile.addEventListener('click', (e) => this.handleOfferSelect(e.currentTarget));
        tile.addEventListener('keydown', (e) => this.handleKeydown(e));
      });

      if (this.isWinbacksFlow) {
        this.persistSelectedOffer();
        if (this.discount) {
          this.persistDiscount(this.discount);
        }
        this.registerSubmitHandlers();

        this.preloadParentSubscription();
      } else {
        this.handleRemoveStoredDiscount();
      }

      if (this.form && this.emailInput) {
        this.form.addEventListener('submit', () => {
          window.localStorage.setItem('chooseOfferEmail', this.emailInput.value);
        });
      }

      this.initExperimentArm();
    }

    /**
     * Entry-flow A/B test branching (control/v1/v2). The arm is resolved
     * client-side by Optimizely (see resolveExperimentArm.js), so we read the
     * current value and also listen for the async decision.
     *   control: keep the server-rendered offer + email entry gate.
     *   v1:      skip the gate entirely and pass through into the builder.
     *   v2:      keep offers, but force the no-email entry path.
     */
    initExperimentArm() {
      // Winbacks has its own dedicated entry flow; leave it untouched.
      if (this.isWinbacksFlow) return;

      this.armApplied = null;
      this.inDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

      // The main page content is hidden-first via CSS (see choose-offer.liquid),
      // with the header left visible, so no lander content paints before the arm
      // resolves. v1 visitors are redirected straight into the builder, so the
      // content must stay hidden for them — revealing it first would flash the
      // lander for a moment before the redirect lands. We therefore only reveal
      // once Optimizely has actually decided on a non-v1 arm (or in the editor).
      this.applyArm(window.experimentArm || 'control');
      window.addEventListener('experimentarm:resolved', () => {
        this.applyArm(window.experimentArm || 'control');
      });

      // Safety net: if Optimizely never decides (script blocked/unavailable),
      // reveal the page after a beat so it isn't left blank.
      this.revealFallbackTimer = window.setTimeout(() => {
        if (this.armApplied === null) this.applyArm('control', { force: true });
      }, 2000);
    }

    applyArm(arm, { force = false } = {}) {
      // v1: redirect into the builder and keep the offer hidden the whole time.
      if (arm === 'v1') {
        if (this.armApplied === 'v1') return;
        this.armApplied = 'v1';
        this.passThroughToBuilder();
        return;
      }

      // control / v2 (and any unknown arm): reveal the page, but only once we
      // trust the arm. Before Optimizely decides, the arm defaults to 'control';
      // revealing then would flash the lander for visitors who turn out to be v1.
      const decided = force || this.inDesignMode || window.experimentArmDecided;
      if (!decided) return;

      if (this.armApplied === arm) return;
      this.armApplied = arm;

      this.revealPage();
      if (arm === 'v2') {
        this.setEntryMode('no-email');
      }
      // control (and any unknown arm): leave the server-rendered markup as-is.
    }

    revealPage() {
      window.clearTimeout(this.revealFallbackTimer);
      document.documentElement.classList.add('exp-arm-ready');
    }

    setEntryMode(mode) {
      if (mode !== 'no-email') return;

      const emailGate = this.querySelector('[data-entry-gate="email"]');
      const noEmailGate = this.querySelector('[data-entry-gate="no-email"]');
      const emailDisclaimer = this.querySelector('[data-email-disclaimer]');

      if (emailGate) emailGate.hidden = true;
      if (emailDisclaimer) emailDisclaimer.hidden = true;
      if (noEmailGate) {
        noEmailGate.hidden = false;
        // Point the CTA at the currently selected offer.
        const selectedTile = this.getSelectedOfferTile();
        if (selectedTile) this.handleOfferSelect(selectedTile);
      }
    }

    passThroughToBuilder() {
      // Never redirect inside the theme editor — it would break section previews.
      if (window.Shopify && window.Shopify.designMode) return;
      if (this.armRedirected) return;
      this.armRedirected = true;

      const params = new URLSearchParams();
      // Carry the lander discount so it persists through the builder to checkout.
      if (this.discount) params.set('reference', this.discount);
      if (this.hasAttribute('data-go-to-step-2')) params.set('step2Redirect', 'true');
      const query = params.toString();
      window.location.assign(query ? `${this.builderUrl}?${query}` : this.builderUrl);
    }

    handleRemoveStoredDiscount() {
      window.localStorage.removeItem('subscriptionBuilderDiscount');
    }

    buildBuilderUrl(variantId, productId) {
      const params = new URLSearchParams();
      params.set('variant-id', variantId);
      params.set('product-id', productId);
      if (this.discount) params.set('reference', this.discount);
      if (this.hasAttribute('data-go-to-step-2')) params.set('step2Redirect', 'true');
      return `${this.builderUrl}?${params.toString()}`;
    }

    handleOfferSelect(tile) {
      this.offerTiles.forEach((t) => {
        t.classList.remove('selected');
        t.setAttribute('aria-checked', 'false');
        t.setAttribute('tabindex', '-1');
        const radio = t.querySelector('[data-offer-radio]');
        if (radio) radio.checked = false;
      });

      tile.classList.add('selected');
      tile.setAttribute('aria-checked', 'true');
      tile.setAttribute('tabindex', '0');

      const radio = tile.querySelector('[data-offer-radio]');

      if (radio) radio.checked = true;

      const variantId = tile.dataset.variantId;
      const productId = tile.dataset.offerProductId;
      const hasValidIds = Boolean(variantId && productId);

      // Keep both entry paths in sync so the experiment arm can swap between
      // them: the email gate's return_to and the no-email CTA's href.
      const returnToInput = this.form?.querySelector('input[name="return_to"]');
      const noEmailLink = this.querySelector('[data-entry-gate="no-email"] a[data-submit]') || this.querySelector('a[data-submit]');

      if (hasValidIds) {
        const builderUrl = this.buildBuilderUrl(variantId, productId);
        if (returnToInput) returnToInput.value = builderUrl;
        if (noEmailLink) {
          noEmailLink.href = builderUrl;
          noEmailLink.removeAttribute('aria-disabled');
          noEmailLink.classList.remove('disabled');
        }
      } else if (noEmailLink) {
        noEmailLink.setAttribute('aria-disabled', 'true');
        noEmailLink.classList.add('disabled');
      }

      this.persistSelectedOffer(tile);
    }

    handleKeydown(e) {
      const tile = e.currentTarget;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleOfferSelect(tile);
        return;
      }

      const isArrowKey = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key);
      if (!isArrowKey) return;

      e.preventDefault();

      const tiles = Array.from(this.offerTiles);
      const currentIndex = tiles.indexOf(tile);

      let nextIndex = currentIndex;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % tiles.length;
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + tiles.length) % tiles.length;
      }

      const nextTile = tiles[nextIndex];
      if (!nextTile) return;

      nextTile.focus();
    }

    registerSubmitHandlers() {
      this.addEventListener('submit', () => {
        this.persistSelectedOffer();
      });

      const submitButton = this.querySelector('[data-submit]');
      if (submitButton) {
        submitButton.addEventListener('click', () => {
          this.persistSelectedOffer();
        });
      }
    }

    getSelectedOfferTile() {
      return this.querySelector('[data-offer-tile].selected') || this.offerTiles[0] || null;
    }

    persistDiscount(discount) {
      try {
        localStorage.setItem(WINBACKS_SELECTED_DISCOUNT_STORAGE_KEY, discount);
      } catch {
        // localStorage can be unavailable in private mode or restricted browsers.
      }
    }

    persistSelectedOffer(tile = null) {
      const selectedTile = tile || this.getSelectedOfferTile();
      if (!selectedTile) return;

      const variantId = selectedTile.dataset.variantId ? String(selectedTile.dataset.variantId) : null;
      const productId = selectedTile.dataset.offerProductId ? String(selectedTile.dataset.offerProductId) : null;
      if (!variantId || !productId) return;

      try {
        localStorage.setItem(
          WINBACKS_SELECTED_OFFER_STORAGE_KEY,
          JSON.stringify({
            variantId,
            productId,
            savedAt: new Date().toISOString(),
          })
        );
      } catch {
        // localStorage can be unavailable in private mode or restricted browsers.
      }
    }

    async waitForRechargeApi() {
      const maxAttempts = 20;
      let attempts = 0;

      while (!window.ButcherBoxRechargeProductCardApi && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts += 1;
      }

      return window.ButcherBoxRechargeProductCardApi || null;
    }

    async preloadParentSubscription() {
      try {
        const rechargeApi = await this.waitForRechargeApi();
        if (!rechargeApi?.getWinbackParentSubscription) return;

        const parentSubscription = await rechargeApi.getWinbackParentSubscription();
        if (!parentSubscription) return;

        localStorage.setItem(
          WINBACKS_PARENT_SUBSCRIPTION_STORAGE_KEY,
          JSON.stringify({
            ...parentSubscription,
            savedAt: new Date().toISOString(),
          })
        );
      } catch (error) {
        console.warn('Unable to preload winbacks parent subscription.', error);
      }
    }
  }

  window.customElements.define('choose-offer-form', ChooseOfferForm);
}
